import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { HashRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import App from './App'
import { AuthProvider } from './auth'
import ErrorBoundary from './components/ErrorBoundary'
import PortalApp, { PortalInvite } from './portal/PortalApp'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      staleTime: 10_000,
      retry: (count, err: any) => (err?.status === 401 || err?.status === 403 ? false : count < 2),
    },
  },
})

/**
 * Кабинет клиента или программа — решается здесь, до всего остального.
 *
 * Не маршрутом внутри App: там уже поднят роутер программы с перенаправлением
 * «всё неизвестное — в журнал», и оно успевало сработать раньше, переписав
 * адрес приглашения. Заодно кабинет не попадает под AuthProvider — это другая
 * учётная запись, и проверять её сотрудником незачем.
 */
function Root() {
  const [hash, setHash] = useState(() => window.location.hash)

  useEffect(() => {
    const onChange = () => setHash(window.location.hash)
    window.addEventListener('hashchange', onChange)
    return () => window.removeEventListener('hashchange', onChange)
  }, [])

  const invite = hash.match(/^#\/portal\/invite\/(.+)$/)
  if (invite) return <PortalInvite token={decodeURIComponent(invite[1])} />
  if (hash === '#/portal' || hash.startsWith('#/portal/') || hash.startsWith('#/portal?')) {
    return <PortalApp />
  }

  // HashRouter, а не BrowserRouter: десктопная сборка открывается с file://,
  // где обычные пути ломаются.
  return (
    <HashRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </HashRouter>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Снаружи всего: ошибка в любом месте должна показать сообщение,
        а не оставить человека перед белым экраном. */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Root />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)
